/**
 * Executive Summary Section Component
 * Provides a scrollable section with sticky header and scroll-down button
 * 
 * Features:
 * - Sticky header that remains visible during scroll
 * - Scroll down button to navigate to bottom of section
 * - Scroll up button when at bottom
 * - Max height with internal scroll
 * 
 * Usage:
 * <ExecutiveSummarySection
 *   title="Sumário Executivo"
 *   subtitle="Análise consolidada do período"
 *   language="pt"
 *   maxHeight="600px"
 * >
 *   {children}
 * </ExecutiveSummarySection>
 */

import React, { useRef, useState, useEffect } from 'react';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';

const ExecutiveSummarySection = ({
  title,
  subtitle,
  language = 'pt',
  maxHeight = '650px',
  icon: Icon = FileText,
  children,
  className = '',
  headerClassName = '',
  dataTestId = 'executive-summary-section',
}) => {
  const contentRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(true);
  const [showScrollUp, setShowScrollUp] = useState(false);

  // Check scroll position to show/hide buttons
  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
      const isAtTop = scrollTop <= 20;
      
      setShowScrollDown(!isAtBottom);
      setShowScrollUp(!isAtTop);
    }
  };

  // Scroll to bottom of section
  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Scroll to top of section
  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Initial check for scroll buttons
  useEffect(() => {
    if (contentRef.current) {
      const { scrollHeight, clientHeight } = contentRef.current;
      // Only show scroll button if content is scrollable
      setShowScrollDown(scrollHeight > clientHeight);
    }
  }, [children]);

  // Default titles based on language
  const defaultTitle = language === 'pt' ? 'Sumário Executivo' : 'Executive Summary';
  const defaultSubtitle = language === 'pt' ? 'Análise consolidada do período' : 'Consolidated period analysis';
  const scrollDownText = language === 'pt' ? 'Ver mais' : 'See more';
  const scrollUpText = language === 'pt' ? 'Voltar ao topo' : 'Back to top';

  return (
    <div 
      className={`bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden relative ${className}`}
      data-testid={dataTestId}
      style={{ maxHeight: `calc(${maxHeight} + 80px)` }}
    >
      {/* Sticky Header */}
      <div className={`sticky top-0 z-20 bg-gradient-to-r from-slate-50 to-white px-6 py-4 border-b border-slate-200 shadow-sm ${headerClassName}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-primary">{title || defaultTitle}</h3>
              {(subtitle || defaultSubtitle) && (
                <p className="text-xs text-slate-500">{subtitle || defaultSubtitle}</p>
              )}
            </div>
          </div>
          
          {/* Scroll buttons - Always visible container */}
          <div className="flex items-center gap-2">
            {showScrollUp && (
              <button
                onClick={scrollToTop}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-all duration-200"
                data-testid="scroll-up-btn"
              >
                <ChevronUp className="w-4 h-4" />
                <span>{scrollUpText}</span>
              </button>
            )}
            {showScrollDown && (
              <button
                onClick={scrollToBottom}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary/90 rounded-full transition-all duration-200 shadow-md hover:shadow-lg"
                data-testid="scroll-down-btn"
              >
                <span>{scrollDownText}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div 
        ref={contentRef}
        onScroll={handleScroll}
        className="overflow-y-auto px-6 py-5"
        style={{ maxHeight }}
      >
        {children}
      </div>

      {/* Bottom gradient fade when more content below */}
      {showScrollDown && (
        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none z-10" />
      )}
    </div>
  );
};

export default ExecutiveSummarySection;
